interface TrackWaiter {
  promise: Promise<MediaStreamTrack>;
  /** Idempotently remove the listener + timer (without settling the promise). */
  cancel: () => void;
}

function createTrackWaiter(
  peerConnection: RTCPeerConnection,
  matches: (event: RTCTrackEvent) => boolean,
  timeoutMessage: string,
  timeoutMs: number,
  signal?: AbortSignal,
): TrackWaiter {
  let cleanup = (): void => {};
  const promise = new Promise<MediaStreamTrack>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("WebRTC track wait aborted"));
      return;
    }

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    const handleTrack = (event: RTCTrackEvent): void => {
      if (!matches(event)) return;
      cleanup();
      resolve(event.track);
    };

    const handleAbort = (): void => {
      cleanup();
      reject(new Error("WebRTC track wait aborted"));
    };

    cleanup = (): void => {
      clearTimeout(timeout);
      peerConnection.removeEventListener("track", handleTrack);
      signal?.removeEventListener("abort", handleAbort);
    };

    peerConnection.addEventListener("track", handleTrack);
    signal?.addEventListener("abort", handleAbort);
  });
  return { promise, cancel: () => cleanup() };
}

/** Register track listeners before applying remote SDP (Cloudflare echo subscriber pattern). */
export function waitForRemoteAudioTracks(
  peerConnection: RTCPeerConnection,
  expectedMids: string[],
  options?: { timeoutMs?: number; onLog?: (message: string) => void; signal?: AbortSignal },
): Promise<MediaStreamTrack[]> {
  const timeoutMs = options?.timeoutMs ?? 20_000;
  const log = options?.onLog ?? (() => {});
  const { signal } = options ?? {};

  if (expectedMids.length === 0) {
    log("No track mids from API; waiting for first audio track event...");
    const waiter = createTrackWaiter(
      peerConnection,
      (event) => event.track.kind === "audio",
      "WebRTC audio track timeout",
      timeoutMs,
      signal,
    );
    return waiter.promise.then((track) => [track]).finally(() => waiter.cancel());
  }

  log(`Waiting for ${expectedMids.length} remote track(s): ${expectedMids.join(", ")}`);
  const waiters = expectedMids.map((mid) =>
    createTrackWaiter(
      peerConnection,
      (event) => event.transceiver.mid === mid,
      `WebRTC track timeout for mid ${mid}`,
      timeoutMs,
      signal,
    ),
  );

  // If any waiter rejects, Promise.all settles immediately; cancel the rest so
  // their `track` listeners and timeouts don't linger until their own timeout.
  return Promise.all(waiters.map((waiter) => waiter.promise)).finally(() => {
    for (const waiter of waiters) waiter.cancel();
  });
}
